/**
 * CÔNG VIỆC.
 *
 * Two tabs:
 *
 * HIỆN TẠI —
 *   1. ĐANG MỜI BẠN NHẬN — offers, each with a live countdown to
 *      `offerExpiresAt` and two big buttons. The countdown is real: the
 *      dispatcher re-offers the task to someone else when it runs out, so a
 *      static "còn 2 phút" would be a lie thirty seconds later.
 *   2. VIỆC CỦA BẠN — accepted / in-progress / paused, with the next legal
 *      action derived from `allowedEvents` (which the API computes from the
 *      same state machine it validates against).
 *
 * LỊCH SỬ — `GET /staff/task-history`: finished work with its KPI outcome
 *   VERBATIM. An event that earned nothing shows exactly WHY (`kpiRejectReason`
 *   as Vietnamese) with a one-tap appeal — an excluded event that is invisible
 *   is the fastest way to destroy trust in a KPI system.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks, useTaskHistory, errorMessage } from '../lib/queries';
import { useServerClock } from '../lib/hooks';
import { useSyncState } from '../lib/hooks';
import {
  isAlreadyClaimed,
  kpiNotice,
  useAcceptTask,
  useRejectTask,
  useStartTask,
} from '../lib/task-actions';
import { formatClock, formatCountdown, formatSignedPointsMilli, formatTime, formatTimeDate } from '../lib/format';
import {
  KPI_REJECT_LABEL,
  REJECT_REASON_LABEL,
  TASK_STATUS_LABEL,
  TASK_STATUS_TONE,
  taskTypeLabel,
  urgencyLabel,
  urgencyTone,
} from '../lib/labels';
import type { OfferedTask, RejectReasonCode, StaffTask, TaskHistoryItem, TaskStatus } from '../lib/contracts';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHeader,
  Sheet,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../components/Toast';

type TasksTab = 'CURRENT' | 'HISTORY';

export function TasksScreen(): JSX.Element {
  const [tab, setTab] = useState<TasksTab>('CURRENT');

  return (
    <div className="space-y-3 pb-8">
      <div className="grid grid-cols-2 gap-2" role="tablist" aria-label="Công việc">
        {(
          [
            { value: 'CURRENT', label: 'Hiện tại' },
            { value: 'HISTORY', label: 'Lịch sử' },
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={tab === option.value}
            onClick={() => setTab(option.value)}
            className={[
              'min-h-touch rounded-2xl border font-semibold active:bg-slate-50',
              option.value === tab ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-700',
            ].join(' ')}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'CURRENT' ? <CurrentTab /> : <HistoryTab />}
    </div>
  );
}

function CurrentTab(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useTasks();
  const now = useServerClock(data?.serverTime, 1000);
  const [rejectTarget, setRejectTarget] = useState<OfferedTask | null>(null);

  if (isLoading) return <LoadingState label="Đang tải danh sách việc…" />;
  if (isError || data === undefined) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  // An offer whose deadline has passed is dead: the dispatcher has already
  // moved on. Hiding it beats letting someone tap "Nhận" into a 409.
  const liveOffers = data.offered.filter((offer) => offer.offerExpiresAt > now);

  return (
    <div className="space-y-3">
      <SectionHeader title={`Đang mời bạn nhận (${liveOffers.length})`} />
      {liveOffers.length === 0 ? (
        <EmptyState
          title="Chưa có việc nào đang mời bạn"
          description="Khi hệ thống điều phối việc cho bạn, việc sẽ hiện ở đây kèm thời gian đếm ngược để phản hồi."
        />
      ) : (
        liveOffers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} now={now} onReject={() => setRejectTarget(offer)} />
        ))
      )}

      <SectionHeader title={`Việc của bạn (${data.accepted.length})`} />
      {data.accepted.length === 0 ? (
        <EmptyState title="Bạn chưa nhận việc nào" />
      ) : (
        data.accepted.map((task) => <AcceptedCard key={task.id} task={task} now={now} />)
      )}

      <RejectSheet target={rejectTarget} onClose={() => setRejectTarget(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function historyStatusLabel(eventType: string): { label: string; tone: ReturnType<typeof urgencyTone> } {
  if (eventType in TASK_STATUS_LABEL) {
    const status = eventType as TaskStatus;
    return { label: TASK_STATUS_LABEL[status], tone: TASK_STATUS_TONE[status] };
  }
  return { label: eventType, tone: 'neutral' };
}

function HistoryTab(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useTaskHistory();

  if (isLoading) return <LoadingState label="Đang tải lịch sử…" />;
  if (isError || data === undefined) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }
  if (data.history.length === 0) {
    return (
      <EmptyState
        title="Chưa có việc nào kết thúc"
        description="Các việc đã hoàn thành, đã từ chối, đã hết hạn hoặc đã huỷ sẽ hiện ở đây kèm kết quả KPI của từng việc."
      />
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader title={`Việc đã kết thúc (${data.count})`} />
      {data.history.map((item) => (
        <HistoryCard key={item.taskEventId} item={item} />
      ))}
    </div>
  );
}

function HistoryCard({ item }: { readonly item: TaskHistoryItem }): JSX.Element {
  const navigate = useNavigate();
  const status = historyStatusLabel(item.eventType);
  const kpiReason =
    item.kpi.rejectReason !== null && item.kpi.rejectReason in KPI_REJECT_LABEL
      ? KPI_REJECT_LABEL[item.kpi.rejectReason as keyof typeof KPI_REJECT_LABEL]
      : item.kpi.rejectReason;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">{item.title}</p>
          <p className="text-sm text-slate-500">{taskTypeLabel(item.taskType)}</p>
        </div>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
        <span className="tabular-nums">{formatTimeDate(item.occurredAt)}</span>
        {item.durationSeconds === null ? null : (
          <span className="tabular-nums">Xử lý {formatClock(item.durationSeconds)}</span>
        )}
        {item.onTime === null ? null : (
          <span className={item.onTime ? 'font-semibold text-emerald-700' : 'font-semibold text-rose-700'}>
            {item.onTime ? 'Đúng hạn' : 'Trễ hạn'}
          </span>
        )}
      </div>

      {item.failureReason === null ? null : (
        <p className="mt-2 text-sm text-slate-700">Lý do: {item.failureReason}</p>
      )}

      {/* KPI outcome, verbatim — counted or not, and WHY when not. */}
      {item.kpi.counted ? (
        <p className="mt-2 rounded-xl bg-emerald-50 p-2 text-sm text-emerald-800">
          Được tính KPI
          {item.kpi.pointsFinalMilli === 0
            ? ' (điểm sẽ do bộ quy tắc chấm sau)'
            : ` · ${formatSignedPointsMilli(item.kpi.pointsFinalMilli)} điểm`}
        </p>
      ) : (
        <div className="mt-2 rounded-xl bg-rose-50 p-2">
          <p className="text-sm font-semibold text-rose-900">Không được tính KPI</p>
          <p className="mt-0.5 text-sm text-rose-800">
            {kpiReason ?? 'Hệ thống không ghi rõ lý do — hãy khiếu nại để được giải thích.'}
          </p>
          <Button
            variant="secondary"
            className="mt-2"
            fullWidth
            onClick={() =>
              navigate('/ca-nhan/khieu-nai', {
                state: { appealType: 'TASK_EVENT', taskEventId: item.taskEventId },
              })
            }
          >
            Khiếu nại thao tác này
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Offer
// ---------------------------------------------------------------------------

function OfferCard({
  offer,
  now,
  onReject,
}: {
  readonly offer: OfferedTask;
  readonly now: number;
  readonly onReject: () => void;
}): JSX.Element {
  const toast = useToast();
  const accept = useAcceptTask();
  const { online } = useSyncState();

  const secondsLeft = Math.max(0, Math.floor((offer.offerExpiresAt - now) / 1000));
  // Under 20s the countdown turns red; the dispatcher is about to move on.
  const critical = secondsLeft <= 20;

  const onAccept = (): void => {
    accept.mutate(offer.id, {
      onSuccess: (result) => {
        const notice = kpiNotice(result.kpiCounted, result.kpiRejectReason);
        toast.success('Đã nhận việc', notice ?? undefined);
      },
      onError: (e) => {
        // Losing the claim race is a NORMAL outcome of a broadcast offer.
        if (isAlreadyClaimed(e)) {
          toast.info('Việc này vừa có người khác nhận', 'Hệ thống sẽ điều phối cho bạn việc khác.');
          return;
        }
        toast.error('Không nhận được việc', errorMessage(e));
      },
    });
  };

  return (
    <Card className={critical ? 'ring-2 ring-rose-400' : ''}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">{offer.title}</p>
          <p className="text-sm text-slate-500">{taskTypeLabel(offer.type)}</p>
        </div>
        <StatusBadge tone={urgencyTone(offer.urgency)}>{urgencyLabel(offer.urgency)}</StatusBadge>
      </div>

      {offer.detail === null ? null : <p className="mt-2 text-sm text-slate-700">{offer.detail}</p>}

      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
        <span className="text-sm text-slate-600">Còn lại để phản hồi</span>
        <span
          className={`text-2xl font-black tabular-nums ${critical ? 'text-rose-600' : 'text-slate-900'}`}
          aria-label={`Còn ${secondsLeft} giây`}
        >
          {formatCountdown(secondsLeft)}
        </span>
      </div>

      {offer.dueAt === null ? null : (
        <p className="mt-2 text-sm text-slate-600">
          Hạn hoàn thành: <strong className="tabular-nums">{formatTime(offer.dueAt)}</strong>
        </p>
      )}

      {!online ? (
        <p className="mt-3 rounded-xl bg-slate-200 p-3 text-sm text-slate-700">
          Đang ngoại tuyến. Nhận/từ chối việc phải gửi ngay lên máy chủ vì nhiều người có thể cùng tranh một
          việc, nên không thể lưu tạm.
        </p>
      ) : null}

      {/* Two big buttons, Nhận on the right where the thumb rests. */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <Button variant="secondary" size="lg" onClick={onReject} disabled={!online || accept.isPending}>
          Từ chối
        </Button>
        <Button
          variant="success"
          size="lg"
          onClick={onAccept}
          loading={accept.isPending}
          disabled={!online || secondsLeft === 0}
        >
          Nhận việc
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Accepted / in-progress
// ---------------------------------------------------------------------------

function AcceptedCard({ task, now }: { readonly task: StaffTask; readonly now: number }): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const start = useStartTask();
  const { online } = useSyncState();

  const canStart = task.allowedEvents.includes('START');
  const canComplete = task.allowedEvents.includes('COMPLETE');
  const overdue = task.dueAt !== null && now > task.dueAt;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">{task.title}</p>
          <p className="text-sm text-slate-500">{taskTypeLabel(task.type)}</p>
        </div>
        <StatusBadge tone={TASK_STATUS_TONE[task.status]}>{TASK_STATUS_LABEL[task.status]}</StatusBadge>
      </div>

      {task.dueAt === null ? null : (
        <p className={`mt-2 text-sm ${overdue ? 'font-bold text-rose-700' : 'text-slate-600'}`}>
          Hạn: <span className="tabular-nums">{formatTime(task.dueAt)}</span>
          {overdue ? ' · đã quá hạn' : ''}
        </p>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Button variant="secondary" size="lg" onClick={() => navigate(`/cong-viec/${task.id}`)}>
          Chi tiết
        </Button>
        {canStart ? (
          <Button
            size="lg"
            loading={start.isPending}
            disabled={!online}
            onClick={() =>
              start.mutate(task.id, {
                onSuccess: (result) => {
                  const notice = kpiNotice(result.kpiCounted, result.kpiRejectReason);
                  toast.success('Đã bắt đầu', notice ?? undefined);
                },
                onError: (e) => toast.error('Không bắt đầu được', errorMessage(e)),
              })
            }
          >
            Bắt đầu
          </Button>
        ) : canComplete ? (
          // Completing needs the evidence flow, so it always goes via detail.
          <Button size="lg" variant="success" onClick={() => navigate(`/cong-viec/${task.id}`)}>
            Hoàn thành
          </Button>
        ) : (
          <Button size="lg" variant="secondary" disabled>
            Không có thao tác
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Reject sheet
// ---------------------------------------------------------------------------

const REJECT_CODES = Object.keys(REJECT_REASON_LABEL) as readonly RejectReasonCode[];

/**
 * Rejecting needs BOTH a coded reason (for KPI and dispatch statistics) and a
 * free-text one (which the API requires in the `x-action-reason` header). The
 * sheet pre-fills the text with the chosen label so the common case is two taps,
 * while still letting someone explain an unusual refusal.
 */
function RejectSheet({
  target,
  onClose,
}: {
  readonly target: OfferedTask | null;
  readonly onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const reject = useRejectTask();
  const [code, setCode] = useState<RejectReasonCode>('BUSY');
  const [text, setText] = useState('');

  const effectiveReason = useMemo(() => {
    const trimmed = text.trim();
    return trimmed.length >= 3 ? trimmed : REJECT_REASON_LABEL[code];
  }, [text, code]);

  if (target === null) return <Sheet open={false} title="" onClose={onClose}>{null}</Sheet>;

  const submit = (): void => {
    reject.mutate(
      { taskId: target.id, reasonCode: code, reason: effectiveReason },
      {
        onSuccess: () => {
          toast.info('Đã từ chối việc', 'Hệ thống sẽ chuyển việc này cho người khác.');
          setText('');
          onClose();
        },
        onError: (e) => {
          if (isAlreadyClaimed(e)) {
            toast.info('Việc này đã đổi trạng thái', 'Có thể người khác đã nhận trước.');
            onClose();
            return;
          }
          toast.error('Không từ chối được', errorMessage(e));
        },
      },
    );
  };

  return (
    <Sheet
      open
      title="Lý do từ chối"
      onClose={onClose}
      footer={
        <Button variant="danger" size="lg" fullWidth loading={reject.isPending} onClick={submit}>
          Gửi từ chối
        </Button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        Việc: <strong>{target.title}</strong>. Lý do được ghi lại và ảnh hưởng tới thống kê điều phối, nên hãy
        chọn đúng.
      </p>

      <ul className="space-y-2">
        {REJECT_CODES.map((value) => (
          <li key={value}>
            <button
              type="button"
              onClick={() => setCode(value)}
              className={[
                'min-h-touch w-full rounded-2xl border px-3 py-2 text-left active:bg-slate-50',
                value === code ? 'border-brand-500 bg-brand-50 font-semibold' : 'border-slate-200',
              ].join(' ')}
            >
              {REJECT_REASON_LABEL[value]}
            </button>
          </li>
        ))}
      </ul>

      <label className="label mt-4" htmlFor="reject-note">
        Mô tả thêm (không bắt buộc)
      </label>
      <textarea
        id="reject-note"
        className="field mt-1"
        rows={3}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={REJECT_REASON_LABEL[code]}
      />
    </Sheet>
  );
}
